import { matchPath } from 'react-router-dom'
import { findAdminAppByPathname } from 'src/constants/adminApps'
import { documentReviewTabTitles } from 'src/constants/document-review/constants'
import { DocumentReviewTab } from 'src/constants/document-review/enums'
import {
  ADMIN_ROUTES,
  buildPartnerDriverPath,
  buildScheduledActionPath,
} from 'src/constants/routes'
import {
  ADMIN_DASHBOARD_CUSTOMER_TABS,
  ADMIN_DASHBOARD_GLOBAL_ITEMS,
  buildCustomerOverviewPath,
  buildCustomerTabPath,
} from 'src/pages/admin-dashboard/adminDashboardNav'
import { PAYMENTS_OPS_ITEMS } from 'src/pages/payments-ops/paymentsOpsNav'
import { REMOTE_CONFIG_ITEMS } from 'src/pages/remote-config/remoteConfigNav'
import type { Crumb } from 'src/constants/adminApps.types'

const HOME_LABEL = 'Home'
const NOT_FOUND_LABEL = 'Not found'
const FLOW_EDITOR_LABEL = 'Onboarding template'
const NEW_FLOW_EDITOR_LABEL = 'New template'
const INSURANCE_POLICY_LABEL = 'Insurance policy'
const INVOICE_DETAILS_LABEL = 'Invoice details'
const SCHEDULED_ACTION_LABEL = 'Scheduled action'
const SCHEDULED_ACTION_RETRIES_LABEL = 'Retries history'
const PARTNER_DRIVER_STEPS_LABEL = 'Collected data steps'
const NEW_BANK_ONBOARDING_LABEL = 'New onboarding'
const BANK_ONBOARDING_LABEL = 'Bank Onboarding'
const NEW_ROLE_LABEL = 'New role'
const ROLE_LABEL = 'Role'
const ROLES_CRUMB_LABEL = 'Roles'
const ROLES_TAB_PATH = `${ADMIN_ROUTES.USERS_MANAGEMENT_URL}?tab=roles`

/** The new-role editor has no `roleId` yet, so the route pattern identifies it. */
const isNewRolePathname = (pathname: string): boolean => {
  return matchPath(ADMIN_ROUTES.USERS_MANAGEMENT_NEW_ROLE_URL, pathname) !== null
}

const isNewBankOnboardingPathname = (pathname: string): boolean => {
  return matchPath(ADMIN_ROUTES.PAYMENTS_OPS_NEW_BANK_ONBOARDING_URL, pathname) !== null
}

/**
 * The retries page sits under a scheduled action and carries the same `configId`, so it cannot be
 * told apart by params alone — matched on the whole route pattern instead.
 */
const isScheduledActionRetriesPathname = (pathname: string): boolean => {
  return matchPath(ADMIN_ROUTES.SCHEDULERS_MANAGEMENT_ACTION_RETRIES_URL, pathname) !== null
}

/** Same reason as above: the steps page carries the driver page's `partnerDriverId`. */
const isPartnerDriverStepsPathname = (pathname: string): boolean => {
  return matchPath(ADMIN_ROUTES.UPDATE_PARTNER_DRIVER_STEPS_URL, pathname) !== null
}
/**
 * The queue tab a document review page belongs to, or `undefined` off those pages. The contractor
 * page is keyed by `stepId`, the master-contractor page by `collectedDataId`.
 */
const resolveDocumentReviewTab = (
  params: Record<string, string | undefined>,
): DocumentReviewTab | undefined => {
  if (params.stepId) {
    return DocumentReviewTab.Contractor
  }

  if (params.collectedDataId) {
    return DocumentReviewTab.MasterContractor
  }

  return undefined
}

const CUSTOMER_OVERVIEW_LABEL = 'Overview'
const ONBOARDING_TEMPLATES_TAB_SLUG = 'onboarding-templates'
const INSURANCE_TAB_SLUG = 'insurance'
const INVOICE_TAB_SLUG = 'invoice'
// The Insurance tab's own h2 is "Insurance policies"; the crumb back to it says the same.
const INSURANCE_POLICIES_CRUMB_LABEL = 'Insurance policies'

/**
 * The flow editor in new-template mode has no `templateId` to key off — nothing is persisted yet —
 * so the route itself identifies it. Matched against the whole route pattern rather than by
 * trailing segment, so an unrelated path that happens to end `/onboarding-templates/new` cannot be
 * mistaken for the editor.
 */
const isNewFlowEditorPathname = (pathname: string): boolean => {
  return matchPath(ADMIN_ROUTES.FLOW_EDITOR_NEW_URL, pathname) !== null
}

/**
 * The customer tab a full-page editor was opened from — the flow editor from Onboarding
 * Templates, a policy from Insurance, an invoice from Invoice. `undefined` for every other route.
 */
const resolveEditorParentTabSlug = (
  pathname: string,
  params: Record<string, string | undefined>,
): string | undefined => {
  if (params.templateId || isNewFlowEditorPathname(pathname)) {
    return ONBOARDING_TEMPLATES_TAB_SLUG
  }

  if (params.insurancePolicyPermanentId) {
    return INSURANCE_TAB_SLUG
  }

  if (params.composedInvoiceId) {
    return INVOICE_TAB_SLUG
  }

  return undefined
}

const buildLeafLabel = (
  pathname: string,
  params: Record<string, string | undefined>,
): string | undefined => {
  // Checked before the tab lookup below: without it the `/new` route resolves to the
  // "Onboarding Templates" tab label and the trail loses its editor leaf entirely.
  if (isNewFlowEditorPathname(pathname)) {
    return NEW_FLOW_EDITOR_LABEL
  }

  if (params.templateId) {
    return FLOW_EDITOR_LABEL
  }

  if (params.insurancePolicyPermanentId) {
    return INSURANCE_POLICY_LABEL
  }

  if (params.composedInvoiceId) {
    return INVOICE_DETAILS_LABEL
  }

  // Checked before the bare `configId` case below: the retries page carries the same param, so
  // without this it would also be labelled "Scheduled action".
  if (isScheduledActionRetriesPathname(pathname)) {
    return SCHEDULED_ACTION_RETRIES_LABEL
  }

  // Without this the scheduled-action detail route resolves to no leaf label at all, and the
  // caller's "not a real route" branch renders the trail as Home / Schedulers Management /
  // Not found — on a page that had loaded correctly.
  if (params.configId) {
    return SCHEDULED_ACTION_LABEL
  }

  // Checked before the bare `partnerDriverId` case: the steps page carries the same param.
  if (isPartnerDriverStepsPathname(pathname)) {
    return PARTNER_DRIVER_STEPS_LABEL
  }

  // The driver page is named by the driver's id — the same id the search row links to — so the
  // trail says which driver is open.
  if (params.partnerDriverId) {
    return params.partnerDriverId
  }

  // The review pages name the document by its id — the same id the queue row links to — so a
  // reviewer can tell which document is open. Without this the trail fell through to Not found.
  if (params.stepId) {
    return params.stepId
  }

  if (params.collectedDataId) {
    return params.collectedDataId
  }

  if (isNewBankOnboardingPathname(pathname)) {
    return NEW_BANK_ONBOARDING_LABEL
  }

  // Checked before the bare `roleId` case: `/roles/new` would otherwise never be told apart from
  // an existing role, and without either case the role editors fall through to Not found.
  if (isNewRolePathname(pathname)) {
    return NEW_ROLE_LABEL
  }

  if (params.roleId) {
    return ROLE_LABEL
  }

  const paymentsOpsItem = PAYMENTS_OPS_ITEMS.find((item) => {
    return item.to === pathname
  })

  if (paymentsOpsItem) {
    return paymentsOpsItem.label
  }

  const remoteConfigItem = REMOTE_CONFIG_ITEMS.find((item) => {
    return item.to === pathname
  })

  if (remoteConfigItem) {
    return remoteConfigItem.label
  }

  const globalItem = ADMIN_DASHBOARD_GLOBAL_ITEMS.find((item) => {
    return item.to === pathname
  })

  if (globalItem) {
    return globalItem.label
  }

  if (!params.customerId) {
    return undefined
  }

  const segments = pathname.split('/').filter(Boolean)
  const customerIdIndex = segments.indexOf(params.customerId)
  const tabSlug = customerIdIndex === -1 ? undefined : segments[customerIdIndex + 1]

  const tab = ADMIN_DASHBOARD_CUSTOMER_TABS.find((item) => {
    return item.slug === tabSlug
  })

  if (tab) {
    return tab.label
  }

  if (!tabSlug) {
    return CUSTOMER_OVERVIEW_LABEL
  }

  return undefined
}

/**
 * Derives the topbar breadcrumb trail from the current route.
 *
 * Inside a customer, the trail is Home / app / customer / page — but the customer segment appears
 * only when the caller passes the loaded customer's display name. Until the customer has been
 * fetched the segment is omitted: a raw id or a fabricated name would both be wrong. The flow
 * editor works the same way with `editorName`: once the template is loaded its name stands in for
 * the generic "Onboarding Templates" crumb, still linking to that tab. An insurance policy keeps
 * the list's own title, "Insurance policies", as its parent crumb instead.
 */
export const buildBreadcrumbs = (
  pathname: string,
  params: Record<string, string | undefined>,
  customerName?: string,
  editorName?: string,
): Crumb[] => {
  if (pathname === ADMIN_ROUTES.LAUNCHER_URL) {
    return [{ label: HOME_LABEL }]
  }

  const home: Crumb = { label: HOME_LABEL, path: ADMIN_ROUTES.LAUNCHER_URL }
  const app = findAdminAppByPathname(pathname)

  if (!app) {
    return [home, { label: NOT_FOUND_LABEL }]
  }

  if (pathname === app.path) {
    return [home, { label: app.title }]
  }

  const leafLabel = buildLeafLabel(pathname, params)

  // The customer crumb links to its Overview; it is present only with a real name (see above).
  const customerCrumbs: Crumb[] =
    params.customerId && customerName
      ? [{ label: customerName, path: buildCustomerOverviewPath(params.customerId) }]
      : []

  // A nested path under a known app with no resolvable label is not a real route —
  // the router renders NotFoundPage for it, so the trail has to agree rather than
  // implying the user is on a valid page. The app crumb stays a link so they can
  // get back into the app rather than only to Home.
  if (!leafLabel) {
    return [home, { label: app.title, path: app.path }, { label: NOT_FOUND_LABEL }]
  }

  // The new-onboarding page sits under Bank Onboarding, which is a linked parent.
  if (isNewBankOnboardingPathname(pathname)) {
    return [
      home,
      { label: app.title, path: app.path },
      { label: BANK_ONBOARDING_LABEL, path: ADMIN_ROUTES.PAYMENTS_OPS_BANK_ONBOARDING_URL },
      { label: leafLabel },
    ]
  }

  // The role editors sit under the Roles tab of Users Management, which is a linked parent.
  if (isNewRolePathname(pathname) || params.roleId) {
    return [
      home,
      { label: app.title, path: app.path },
      { label: ROLES_CRUMB_LABEL, path: ROLES_TAB_PATH },
      { label: leafLabel },
    ]
  }

  // The retries page is one level below the action it belongs to, so the action sits between the
  // app and the leaf as a linked parent rather than the trail jumping straight from the app.
  if (isScheduledActionRetriesPathname(pathname) && params.configId) {
    return [
      home,
      { label: app.title, path: app.path },
      { label: SCHEDULED_ACTION_LABEL, path: buildScheduledActionPath(params.configId) },
      { label: leafLabel },
    ]
  }

  // The steps page is one level below its driver, so the driver sits between the app and the leaf
  // as a linked parent.
  if (isPartnerDriverStepsPathname(pathname) && params.partnerDriverId) {
    return [
      home,
      { label: app.title, path: app.path },
      { label: params.partnerDriverId, path: buildPartnerDriverPath(params.partnerDriverId) },
      { label: leafLabel },
    ]
  }

  // A review page sits under one of the queue's tabs, so that tab is a linked parent — it opens
  // the queue on the same tab the document came from.
  const documentReviewTab = resolveDocumentReviewTab(params)

  if (documentReviewTab) {
    return [
      home,
      { label: app.title, path: app.path },
      {
        label: documentReviewTabTitles[documentReviewTab],
        path: `${ADMIN_ROUTES.INTERNAL_DOCUMENT_REVIEW_URL}?tab=${documentReviewTab}`,
      },
      { label: leafLabel },
    ]
  }

  // A full-page editor is not itself a tab, so the trail names the tab it was opened from (or
  // the loaded record's name in its place) as a linked parent — Back and the trail both use it.
  const editorParentTabSlug = resolveEditorParentTabSlug(pathname, params)

  if (editorParentTabSlug && params.customerId) {
    const parentTab = ADMIN_DASHBOARD_CUSTOMER_TABS.find((tab) => {
      return tab.slug === editorParentTabSlug
    })

    if (parentTab) {
      const parentLabel =
        parentTab.slug === INSURANCE_TAB_SLUG ? INSURANCE_POLICIES_CRUMB_LABEL : parentTab.label

      return [
        home,
        { label: app.title, path: app.path },
        ...customerCrumbs,
        {
          label: editorName ?? parentLabel,
          path: buildCustomerTabPath(params.customerId, parentTab.slug),
        },
        { label: leafLabel },
      ]
    }
  }

  return [home, { label: app.title, path: app.path }, ...customerCrumbs, { label: leafLabel }]
}

/**
 * Where the top bar's Back button goes: the previous SCREEN, not the previous crumb. The
 * screens are the launcher, an app's top-level pages (Customer list, Insurance Products, LMS
 * Courses), a customer (its Overview and tabs share one rail and count as one screen), and the
 * full-page editors reached from a customer tab (the flow editor, an insurance policy, an
 * invoice).
 *
 * So from any customer tab Back leaves the customer for the list — it never hops between the
 * customer's own tabs — and from a top-level page it returns to the launcher. Only the editors
 * go up one crumb, to the tab that opened them.
 */
export const resolveBreadcrumbBackPath = (
  crumbs: Crumb[],
  pathname: string,
  params: Record<string, string | undefined>,
): string => {
  const isFullPageEditor = resolveEditorParentTabSlug(pathname, params) !== undefined

  if (isFullPageEditor) {
    const linkedPaths = crumbs
      .map((crumb) => {
        return crumb.path
      })
      .filter((path): path is string => {
        return Boolean(path) && path !== pathname
      })

    return linkedPaths[linkedPaths.length - 1] ?? ADMIN_ROUTES.LAUNCHER_URL
  }

  if (params.customerId) {
    return ADMIN_ROUTES.ADMIN_DASHBOARD_CUSTOMERS_URL
  }

  return ADMIN_ROUTES.LAUNCHER_URL
}
